import React, { Component } from 'react';
import GoogleDocsViewer from 'react-google-docs-viewer';

class UniversalDocumentViewer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            fileType: this.detectFileType(props.fileUrl),
            showDownloadOnly: false, // Para mostrar solo link de descarga si falla o es muy grande
            isCheckingSize: true, // Verificando tamaño del archivo
            fileSizeMB: 0, // Tamaño del archivo en MB
        };
    }

    componentDidMount() {
        // Para PDFs, verificar el tamaño del archivo
        if (this.state.fileType === 'pdf' && this.props.fileUrl) {
            this.checkFileSize(this.props.fileUrl);
        } else {
            this.setState({ isCheckingSize: false });
        }
    }

    checkFileSize = async (url) => {
        try {
            const response = await fetch(url, { method: 'HEAD' });
            const contentLength = response.headers.get('Content-Length');

            if (contentLength) {
                const fileSizeBytes = parseInt(contentLength, 10);
                const fileSizeMB = fileSizeBytes / (1024 * 1024);

                // Si el archivo es mayor a 15MB, mostrar descarga directamente
                this.setState({
                    fileSizeMB: fileSizeMB,
                    showDownloadOnly: fileSizeMB > 15,
                    isCheckingSize: false
                });
            } else {
                // Si no se puede obtener el tamaño, intentar mostrar el visor
                this.setState({ isCheckingSize: false });
            }
        } catch (error) {
            console.error('Error checking file size:', error);
            // Si hay error, intentar mostrar el visor de todas formas
            this.setState({ isCheckingSize: false });
        }
    }

    detectFileType(url) {
        if (!url) return 'unknown';
        const extension = url.split('.').pop().toLowerCase().split('?')[0];

        const fileTypes = {
            pdf: 'pdf',
            // Documentos Word
            doc: 'word',
            docx: 'word',
            // Documentos Excel
            xls: 'excel',
            xlsx: 'excel',
            // Documentos PowerPoint
            ppt: 'powerpoint',
            pptx: 'powerpoint',
            // Imágenes
            jpg: 'image',
            jpeg: 'image',
            png: 'image',
            gif: 'image',
            webp: 'image',
            bmp: 'image',
            svg: 'image',
            // Otros
            txt: 'text',
            csv: 'csv'
        };

        return fileTypes[extension] || 'unknown';
    }

    renderPdfViewer() {
        const { fileUrl, width = 900, height = 780 } = this.props;
        const { showDownloadOnly, isCheckingSize, fileSizeMB } = this.state;

        // Mostrar loader mientras se verifica el tamaño
        if (isCheckingSize) {
            return (
                <div className="universal-viewer-container">
                    <div className="alert alert-info m-3 text-center" role="alert">
                        <div className="spinner-border text-primary mb-3" role="status">
                            <span className="sr-only">Verificando tamaño del archivo...</span>
                        </div>
                        <p className="mb-0">Verificando tamaño del archivo...</p>
                    </div>
                </div>
            );
        }

        // Si el usuario pidió solo descarga o si el archivo es mayor a 15MB
        if (showDownloadOnly) {
            const autoDetected = fileSizeMB > 15;
            return (
                <div className="universal-viewer-container">
                    <div className="alert alert-warning m-3 text-center" role="alert">
                        <h5><i className="fas fa-file-pdf mr-2"></i>Documento PDF</h5>
                        {autoDetected ? (
                            <p className="mb-3">
                                <strong>Este archivo es muy grande ({fileSizeMB.toFixed(2)} MB)</strong> para visualizarse en línea.
                            </p>
                        ) : (
                            <p className="mb-3">
                                Este archivo es muy grande para visualizarse en línea o el visor no está disponible.
                            </p>
                        )}
                        <p className="mb-3">
                            <small className="text-muted">
                                Los archivos mayores a 15 MB no se cargan correctamente en el visualizador.
                            </small>
                        </p>
                        <a
                            href={fileUrl}
                            download
                            className="btn btn-primary btn-lg"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <i className="fas fa-download mr-2"></i>
                            Descargar archivo PDF {fileSizeMB > 0 && `(${fileSizeMB.toFixed(2)} MB)`}
                        </a>
                        {!autoDetected && (
                            <>
                                <br/>
                                <button
                                    className="btn btn-link mt-3"
                                    onClick={() => this.setState({ showDownloadOnly: false })}
                                >
                                    <i className="fas fa-redo mr-1"></i>
                                    Intentar visualizar de nuevo
                                </button>
                            </>
                        )}
                    </div>
                </div>
            );
        }

        // Intentar mostrar con GoogleDocsViewer
        return (
            <div className="universal-viewer-container">
                {/* Controles */}
                <div className="universal-viewer-controls mb-3 d-flex justify-content-between align-items-center flex-wrap">
                    <div className="m-2">
                        <span className="badge badge-info mr-2">
                            <i className="fas fa-file-pdf mr-1"></i>
                            Documento PDF {fileSizeMB > 0 && `(${fileSizeMB.toFixed(2)} MB)`}
                        </span>
                    </div>

                    <div className="m-2">
                        <a
                            href={fileUrl}
                            download
                            className="btn btn-sm btn-outline-primary"
                            title="Descargar documento"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <i className="fas fa-download"></i> Descargar
                        </a>
                    </div>
                </div>

                {/* Google Docs Viewer */}
                <div className="universal-viewer-document text-center" style={{ maxHeight: height + 'px', overflow: 'auto' }}>
                    <GoogleDocsViewer
                        width={width + 'px'}
                        height={height + 'px'}
                        fileUrl={fileUrl}
                    />
                </div>

                <div className="text-center mt-2 text-muted small d-flex justify-content-center align-items-center">
                    <i className="fas fa-info-circle mr-2"></i>
                    <span className="mr-2">Si el documento no carga o es muy grande, descárgalo:</span>
                    <a
                        href={fileUrl}
                        download
                        className="btn btn-sm btn-primary"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <i className="fas fa-download mr-1"></i>
                        Descargar
                    </a>
                </div>
            </div>
        );
    }

    renderOfficeViewer() {
        const { fileUrl, width = 900, height = 780 } = this.props;
        const { fileType } = this.state;

        // URL del visor de Microsoft Office
        const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`;

        const fileTypeLabels = {
            word: 'Word',
            excel: 'Excel',
            powerpoint: 'PowerPoint'
        };

        const fileTypeIcons = {
            word: 'fa-file-word',
            excel: 'fa-file-excel',
            powerpoint: 'fa-file-powerpoint'
        };

        return (
            <div className="universal-viewer-container">
                {/* Controles */}
                <div className="universal-viewer-controls mb-3 d-flex justify-content-between align-items-center flex-wrap">
                    <div className="m-2">
                        <span className="badge badge-primary mr-2">
                            <i className={`fas ${fileTypeIcons[fileType] || 'fa-file-alt'} mr-1`}></i>
                            Documento {fileTypeLabels[fileType] || fileType}
                        </span>
                    </div>

                    <a
                        href={fileUrl}
                        download
                        className="btn btn-sm btn-outline-primary m-2"
                        title="Descargar documento"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <i className="fas fa-download"></i> Descargar
                    </a>
                </div>

                {/* Visor de Microsoft Office */}
                <div className="universal-viewer-document" style={{ height: height + 'px' }}>
                    <iframe
                        src={officeViewerUrl}
                        width={width}
                        height={height}
                        frameBorder="0"
                        title={`Visor de ${fileTypeLabels[fileType]}`}
                        style={{ border: 'none', display: 'block', margin: '0 auto' }}
                    >
                        Tu navegador no soporta iframes.
                    </iframe>
                    <div className="text-center mt-2 text-muted small">
                        <i className="fas fa-info-circle mr-1"></i>
                        Visualización proporcionada por Microsoft Office Online
                    </div>
                </div>
            </div>
        );
    }

    renderImageViewer() {
        const { fileUrl, width = 900 } = this.props;

        return (
            <div className="universal-viewer-container">
                <div className="universal-viewer-controls mb-3 d-flex justify-content-end">
                    <a
                        href={fileUrl}
                        download
                        className="btn btn-sm btn-outline-primary"
                        title="Descargar imagen"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <i className="fas fa-download"></i> Descargar
                    </a>
                </div>

                <div className="universal-viewer-document text-center">
                    <img
                        src={fileUrl}
                        alt="Documento"
                        style={{ maxWidth: width + 'px', width: '100%', height: 'auto' }}
                        className="img-fluid shadow"
                        onError={(e) => {
                            e.target.style.display = 'none';
                            this.setState({ error: 'Error al cargar la imagen' });
                        }}
                    />
                </div>
            </div>
        );
    }

    renderTextViewer() {
        const { fileUrl } = this.props;

        return (
            <div className="universal-viewer-container">
                <div className="alert alert-info m-3" role="alert">
                    <h5><i className="fas fa-file-alt mr-2"></i>Archivo de texto</h5>
                    <p className="mb-3">
                        Este archivo de texto se puede descargar para visualizarlo.
                    </p>
                    <a
                        href={fileUrl}
                        download
                        className="btn btn-primary"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <i className="fas fa-download mr-1"></i>
                        Descargar archivo
                    </a>
                </div>
            </div>
        );
    }

    renderUnknownViewer() {
        const { fileUrl } = this.props;
        const fileName = fileUrl.split('/').pop().split('?')[0];

        return (
            <div className="universal-viewer-container">
                <div className="alert alert-warning m-3" role="alert">
                    <h5><i className="fas fa-file mr-2"></i>Archivo: {fileName}</h5>
                    <p className="mb-3">
                        Este tipo de archivo no puede previsualizarse en el navegador.
                    </p>
                    <a
                        href={fileUrl}
                        download
                        className="btn btn-primary"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <i className="fas fa-download mr-1"></i>
                        Descargar archivo
                    </a>
                </div>
            </div>
        );
    }

    renderNoDocument() {
        return (
            <div className="alert alert-info m-3" role="alert">
                <i className="fas fa-info-circle mr-2"></i>
                No hay documento adjunto
            </div>
        );
    }

    render() {
        const { fileUrl } = this.props;
        const { fileType } = this.state;

        if (!fileUrl) {
            return this.renderNoDocument();
        }

        switch (fileType) {
            case 'pdf':
                return this.renderPdfViewer();

            case 'word':
            case 'excel':
            case 'powerpoint':
                return this.renderOfficeViewer();

            case 'image':
                return this.renderImageViewer();

            case 'text':
            case 'csv':
                return this.renderTextViewer();

            default:
                return this.renderUnknownViewer();
        }
    }
}

export default UniversalDocumentViewer;
